import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CreditCard, CheckCircle2, XCircle, AlertTriangle, ArrowUpRight,
  Calendar, Shield, Loader2, ChevronRight, Clock, RefreshCw,
  FileText, DollarSign, Package, X, BadgeCheck, Zap
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_COLORS = {
  small_business: { bg: 'bg-sky-50', border: 'border-sky-200', badge: 'bg-sky-100 text-sky-700', accent: 'text-sky-600' },
  full_business:  { bg: 'bg-emerald-50', border: 'border-emerald-200', badge: 'bg-emerald-100 text-emerald-700', accent: 'text-emerald-600' },
  enterprise:     { bg: 'bg-violet-50', border: 'border-violet-200', badge: 'bg-violet-100 text-violet-700', accent: 'text-violet-600' },
  complete:       { bg: 'bg-amber-50', border: 'border-amber-200', badge: 'bg-amber-100 text-amber-700', accent: 'text-amber-600' },
};

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

function formatCurrency(amount) {
  if (amount == null) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

// ── Cancel Confirmation Dialog ──
function CancelDialog({ sub, onConfirm, onDismiss, loading }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full mx-4 space-y-5" data-testid="cancel-dialog">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Cancel Subscription</h3>
              <p className="text-xs text-gray-500">{sub?.tier_name}</p>
            </div>
          </div>
          <button onClick={onDismiss} className="p-1 hover:bg-gray-100 rounded-lg" data-testid="cancel-dialog-close">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        <div className="bg-red-50 border border-red-200 rounded-xl p-4 space-y-2">
          <p className="text-sm text-red-700 font-medium">You will lose access to:</p>
          <ul className="space-y-1">
            {(sub?.active_modules || []).map(m => (
              <li key={m} className="text-sm text-red-600 flex items-center gap-2">
                <X className="w-3 h-3" /> {m}
              </li>
            ))}
          </ul>
        </div>

        <p className="text-sm text-gray-600">
          Your subscription will be cancelled immediately. This action cannot be undone.
          You can resubscribe at any time from the pricing page.
        </p>

        <div className="flex gap-3">
          <Button variant="outline" onClick={onDismiss} className="flex-1" disabled={loading} data-testid="cancel-dismiss-btn">
            Keep Subscription
          </Button>
          <Button
            onClick={onConfirm}
            disabled={loading}
            className="flex-1 bg-red-600 hover:bg-red-700 text-white"
            data-testid="cancel-confirm-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Yes, Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── No Subscription State ──
function NoSubscription() {
  const navigate = useNavigate();
  return (
    <div className="flex flex-col items-center justify-center py-24 space-y-6" data-testid="no-subscription-state">
      <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center">
        <Package className="w-10 h-10 text-slate-400" />
      </div>
      <div className="text-center space-y-2">
        <h2 className="text-xl font-semibold text-gray-800">No Active Subscription</h2>
        <p className="text-gray-500 max-w-sm">
          You're not currently subscribed to any plan. Choose a plan to unlock the full power of AI Blue Skies Cloud.
        </p>
      </div>
      <Button
        onClick={() => navigate('/admin/pricing')}
        className="bg-sky-600 hover:bg-sky-700 text-white px-6"
        data-testid="view-plans-btn"
      >
        <Zap className="w-4 h-4 mr-2" />
        View Plans
      </Button>
    </div>
  );
}

// ── Main Page ──
export default function SubscriptionPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subData, setSubData] = useState(null);
  const [billingHistory, setBillingHistory] = useState([]);
  const [showCancel, setShowCancel] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [subRes, histRes] = await Promise.all([
        fetch(`${API_URL}/api/pricing/my-subscription`),
        fetch(`${API_URL}/api/pricing/billing-history`),
      ]);
      const subJson = await subRes.json();
      const histJson = await histRes.json();
      setSubData(subJson);
      setBillingHistory(histJson.transactions || []);
    } catch {
      toast.error('Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    const subId = subData?.subscription?.subscription_id;
    if (!subId) return;
    setCancelling(true);
    try {
      const res = await fetch(`${API_URL}/api/pricing/cancel-subscription/${subId}`, {
        method: 'POST',
      });
      if (!res.ok) throw new Error('Failed to cancel');
      toast.success('Subscription cancelled successfully');
      setShowCancel(false);
      await loadData();
    } catch {
      toast.error('Failed to cancel subscription. Please try again.');
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="w-8 h-8 animate-spin text-sky-500" />
      </div>
    );
  }

  const isSubscribed = subData?.subscribed;
  const sub = subData?.subscription;
  const tier = subData?.tier_details;
  const tierKey = sub?.tier_id;
  const colors = TIER_COLORS[tierKey] || TIER_COLORS.small_business;

  return (
    <div className="max-w-4xl mx-auto space-y-6 py-6" data-testid="subscription-page">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Subscription</h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage your AI Blue Skies Cloud plan</p>
        </div>
        <Button variant="outline" size="sm" onClick={loadData} data-testid="refresh-btn">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {!isSubscribed ? (
        <NoSubscription />
      ) : (
        <>
          {/* Current Plan Card */}
          <Card className={`border-2 ${colors.border} ${colors.bg}`} data-testid="current-plan-card">
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <BadgeCheck className={`w-5 h-5 ${colors.accent}`} />
                      <span className="text-sm font-medium text-gray-500 uppercase tracking-wide">Current Plan</span>
                    </div>
                    {tier?.badge && (
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${colors.badge}`}>
                        {tier.badge}
                      </span>
                    )}
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700" data-testid="plan-status">
                      Active
                    </span>
                  </div>

                  <h2 className="text-2xl font-bold text-gray-900" data-testid="plan-name">
                    {tier?.name || sub?.tier_name}
                  </h2>

                  <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                    <div className="flex items-center gap-1.5">
                      <DollarSign className="w-4 h-4 text-gray-400" />
                      <span data-testid="plan-price">
                        {formatCurrency(sub?.total_price)}
                        <span className="text-gray-400 ml-1">/ {sub?.billing_cycle || 'month'}</span>
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span>Started {formatDate(sub?.start_date)}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <CreditCard className="w-4 h-4 text-gray-400" />
                      <span className="font-mono text-xs text-gray-400">{sub?.subscription_id}</span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-2 min-w-[160px]">
                  <Button
                    onClick={() => navigate('/admin/pricing')}
                    className="bg-sky-600 hover:bg-sky-700 text-white"
                    data-testid="upgrade-plan-btn"
                  >
                    <ArrowUpRight className="w-4 h-4 mr-2" />
                    Change Plan
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowCancel(true)}
                    className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
                    data-testid="cancel-plan-btn"
                  >
                    Cancel Plan
                  </Button>
                </div>
              </div>

              {/* Active Modules */}
              {sub?.active_modules?.length > 0 && (
                <div className="mt-5 pt-5 border-t border-gray-200">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                    <Shield className="w-3.5 h-3.5" /> Active Modules
                  </p>
                  <div className="flex flex-wrap gap-2" data-testid="active-modules">
                    {sub.active_modules.map(m => (
                      <span key={m} className={`px-2.5 py-1 text-xs font-medium rounded-full ${colors.badge}`}>
                        {m}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Plan Features */}
              {tier?.core_features?.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                    Included Features
                  </p>
                  <ul className="grid sm:grid-cols-2 gap-1.5">
                    {tier.core_features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                        <CheckCircle2 className={`w-4 h-4 mt-0.5 shrink-0 ${colors.accent}`} />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Billing History */}
          <Card data-testid="billing-history-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <FileText className="w-4 h-4 text-gray-400" />
                Billing History
              </CardTitle>
            </CardHeader>
            <CardContent>
              {billingHistory.length === 0 ? (
                <div className="text-center py-10 text-gray-400">
                  <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No billing records yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="billing-table">
                    <thead>
                      <tr className="border-b border-gray-100">
                        <th className="text-left py-2 px-2 text-xs font-semibold text-gray-400 uppercase">Date</th>
                        <th className="text-left py-2 px-2 text-xs font-semibold text-gray-400 uppercase">Plan</th>
                        <th className="text-left py-2 px-2 text-xs font-semibold text-gray-400 uppercase">Billing</th>
                        <th className="text-right py-2 px-2 text-xs font-semibold text-gray-400 uppercase">Amount</th>
                        <th className="text-center py-2 px-2 text-xs font-semibold text-gray-400 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {billingHistory.map((tx, i) => (
                        <tr key={tx.session_id || i} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                          <td className="py-3 px-2 text-gray-600">
                            {formatDate(tx.paid_at || tx.created_at)}
                          </td>
                          <td className="py-3 px-2 font-medium text-gray-800">{tx.tier_name}</td>
                          <td className="py-3 px-2 text-gray-500 capitalize">{tx.billing_cycle || 'monthly'}</td>
                          <td className="py-3 px-2 text-right font-semibold text-gray-800">
                            {formatCurrency(tx.total_amount)}
                          </td>
                          <td className="py-3 px-2 text-center">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                              <CheckCircle2 className="w-3 h-3" /> Paid
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Support Note */}
          <div className="text-center text-xs text-gray-400 pb-4">
            Need help? Contact support with your subscription ID: <span className="font-mono">{sub?.subscription_id}</span>
          </div>
        </>
      )}

      {/* Cancel Dialog */}
      {showCancel && (
        <CancelDialog
          sub={sub}
          onConfirm={handleCancel}
          onDismiss={() => setShowCancel(false)}
          loading={cancelling}
        />
      )}
    </div>
  );
}
